import {
    PropertyManagementCompany,
    User,
    ApartmentComplex,
    Customer,
    DatabaseComplex,
    DatabaseUser,
    DatabaseCompany,
    DatabaseAnonymousUpload,
    UserRole,
} from "../types";
import { supabase } from "./supabaseClient";
import {
    transformDatabaseUser,
    transformDatabaseUsers,
    transformDatabaseComplex,
    transformDatabaseCompany,
    transformDatabaseAnonymousUpload,
    transformUploadsToCustomers,
} from "./dataTransformers";
import { AnonymousUpload } from "../types";
import { logger } from "../lib/logger";
import { TIME_MS } from "../constants";
import { withRetry } from "./errorHandler";

/**
 * Untyped RPC caller for DB functions not yet in generated Supabase types.
 * Many RPC functions exist in the database but weren't captured during type generation.
 * TODO: Regenerate types after ensuring all RPC functions are discoverable.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rpc: (fn: string, params?: Record<string, unknown>, signal?: AbortSignal) => PromiseLike<{ data: any; error: any }> =
    (fn, params, signal) => {
        const query = supabase.rpc(fn as never, params as never);
        return signal ? query.abortSignal(signal) : query;
    };

// ============================================
// INTERFACES (Item 17 - dataService optimization)
// ============================================

/** User statistics for a company */
export interface UserStatistics {
    totalUsers: number;
    usersByRole: Record<UserRole, number>;
    usersAddedThisWeek: number;
    usersAddedThisMonth: number;
    averageComplexesPerUser: number;
}

/** Paginated users result */
export interface PaginatedUsersResult {
    users: User[];
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
}

// ============================================
// ITEM 10: RPC-based user interfaces
// ============================================

/** Platform-wide user statistics (SUPER_ADMIN only) - from RPC */
export interface PlatformUserStatistics {
    totalUsers: number;
    activeUsers: number;
    inactiveUsers: number;
    usersByRole: Record<string, number>;
    usersThisWeek: number;
    usersThisMonth: number;
    emailConfirmedCount: number;
    avgComplexesPerUser: number;
}

/** User search result with similarity score - from RPC */
export interface UserSearchResult {
    id: string;
    name: string;
    email: string;
    role: string;
    companyId: string | null;
    isActive: boolean;
    similarityScore: number;
}

/** RPC-based paginated users result */
export interface RPCPaginatedUsersResult {
    users: User[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

/** User activity summary for monitoring */
export interface UserActivitySummary {
    totalUsers: number;
    activeInPeriod: number;
    inactiveInPeriod: number;
    neverLoggedIn: number;
    activityRate: number;
}

// ============================================
// COMPANY INTERFACES (Item 9 - companies table optimization)
// ============================================

/** Platform-wide company statistics (SUPER_ADMIN only) */
export interface CompanyStatistics {
    totalCompanies: number;
    totalComplexes: number;
    totalUsers: number;
    companiesThisMonth: number;
    avgComplexesPerCompany: number;
    avgUsersPerCompany: number;
}

/** Detailed statistics for a specific company */
export interface CompanyDetailStatistics {
    companyName: string;
    totalComplexes: number;
    totalUsers: number;
    totalUploads: number;
    completedUploads: number;
    pendingUploads: number;
    totalRevenue: number;
}

/** Company search result with fuzzy match score */
export interface CompanySearchResult {
    id: string;
    name: string;
    ownerName: string;
    ownerEmail: string | null;
    similarityScore: number;
}

/** Paginated companies result */
export interface PaginatedCompaniesResult {
    companies: PropertyManagementCompany[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

// ============================================
// ITEM 11: RPC-based complex interfaces
// ============================================

/** Platform-wide complex statistics (SUPER_ADMIN only) - from RPC */
export interface PlatformComplexStatistics {
    totalComplexes: number;
    complexesWithUploads: number;
    complexesWithoutUploads: number;
    totalUploads: number;
    avgUploadsPerComplex: number;
    complexesThisMonth: number;
}

/** Complex statistics for a specific company - from RPC */
export interface CompanyComplexStatistics {
    companyName: string;
    totalComplexes: number;
    totalUploads: number;
    pendingUploads: number;
    completedUploads: number;
    flaggedUploads: number;
    avgUploadsPerComplex: number;
    totalRevenue: number;
}

/** Complex search result with fuzzy match score - from RPC */
export interface ComplexSearchResult {
    id: string;
    name: string;
    address: string;
    companyId: string;
    companyName: string;
    uploadCount: number;
    similarityScore: number;
}

/** RPC-based paginated complexes result */
export interface RPCPaginatedComplexesResult {
    complexes: Array<{
        id: string;
        name: string;
        address: string;
        companyId: string;
        companyName: string;
        totalCustomers: number;
        uploadCount: number;
        pendingCount: number;
        completedCount: number;
        createdAt: string;
        updatedAt: string;
    }>;
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

/** Complex upload summary for dashboard cards - from RPC */
export interface ComplexUploadSummary {
    complexId: string;
    complexName: string;
    complexAddress: string;
    totalUploads: number;
    pendingUploads: number;
    processingUploads: number;
    completedUploads: number;
    failedUploads: number;
    verifiedCount: number;
    flaggedCount: number;
    totalRevenue: number;
    avgAuthenticityScore: number;
    lastUploadAt: string | null;
}

// ============================================
// ITEM 12: CUSTOMER TABLE INTERFACES
// ============================================

/** Customer statistics for a specific complex - from RPC */
export interface ComplexCustomerStatistics {
    complexName: string;
    totalCustomers: number;
    pendingCustomers: number;
    approvedCustomers: number;
    rejectedCustomers: number;
    totalRevenue: number;
    avgAmountPaid: number;
    customersThisMonth: number;
    approvalRate: number;
}

/** Company-wide customer statistics - from RPC */
export interface CompanyCustomerStatistics {
    companyName: string;
    totalCustomers: number;
    pendingCustomers: number;
    approvedCustomers: number;
    rejectedCustomers: number;
    totalRevenue: number;
    avgAmountPaid: number;
    customersThisWeek: number;
    customersThisMonth: number;
    approvalRate: number;
    complexCount: number;
}

/** Customer search result with fuzzy match score - from RPC */
export interface CustomerSearchResult {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    status: 'pending' | 'approved' | 'rejected';
    complexId: string;
    complexName: string;
    amountPaid: number;
    dateSubmitted: string;
    similarityScore: number;
}

/** RPC-based paginated customers result */
export interface RPCPaginatedCustomersResult {
    customers: Array<{
        id: string;
        name: string;
        email: string | null;
        phone: string | null;
        status: 'pending' | 'approved' | 'rejected';
        dateSubmitted: string;
        amountPaid: number;
        notes: string | null;
        approvedBy: string | null;
        approvedAt: string | null;
        complexId: string;
        complexName: string;
        createdAt: string;
        updatedAt: string;
    }>;
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

/** Pending customer for review queue - from RPC */
export interface PendingCustomerForReview {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    complexId: string;
    complexName: string;
    companyId: string;
    dateSubmitted: string;
    amountPaid: number;
    notes: string | null;
    daysPending: number;
}

// ============================================
// ITEM 14: DATABASE ADMIN INTERFACES
// ============================================

/** Index usage statistics - from RPC get_index_usage_stats() */
export interface IndexUsageStats {
    schemaName: string;
    tableName: string;
    indexName: string;
    indexSize: string;
    idxScan: number;
    idxTupRead: number;
    idxTupFetch: number;
    indexDefinition: string;
}

/** Unused index information - from RPC get_unused_indexes() */
export interface UnusedIndexInfo {
    tableName: string;
    indexName: string;
    indexSize: string;
    scanCount: number;
    recommendation: string;
}

/** Missing foreign key index - from RPC get_missing_fk_indexes() */
export interface MissingFkIndex {
    tableName: string;
    fkName: string;
    fkColumns: string;
    recommendation: string;
}

/** Table statistics - from RPC get_table_statistics() */
export interface TableStatistics {
    tableName: string;
    rowCount: number;
    tableSize: string;
    indexSize: string;
    totalSize: string;
    indexesCount: number;
}

/** Index health report item - from RPC get_index_health_report() */
export interface IndexHealthItem {
    category: string;
    tableName: string;
    itemName: string;
    currentValue: string;
    recommendation: string;
    priority: 'high' | 'medium' | 'low';
}

// ============================================
// ITEM 15: STORAGE BUCKET INTERFACES
// ============================================

/** Storage bucket statistics - from RPC get_storage_bucket_stats() */
export interface StorageBucketStats {
    bucketId: string;
    totalFiles: number;
    totalSizeBytes: number;
    totalSizeMb: number;
    avgFileSizeBytes: number;
    oldestFile: Date | null;
    newestFile: Date | null;
    filesByMonth: Array<{ month: string; count: number; size_mb: number }>;
}

/** Orphaned storage file - from RPC get_orphaned_storage_files() */
export interface OrphanedStorageFile {
    filePath: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    createdAt: Date;
    lastAccessedAt: Date | null;
}

/** Storage access log entry - from RPC get_storage_access_log() */
export interface StorageAccessLogEntry {
    filePath: string;
    uploadId: string | null;
    uploadEmail: string | null;
    accessCount: number;
    lastAccessedAt: Date | null;
    createdAt: Date;
    processingStatus: string | null;
}

/** Company storage usage - from RPC get_storage_usage_by_company() */
export interface CompanyStorageUsage {
    companyId: string;
    companyName: string;
    totalFiles: number;
    totalSizeBytes: number;
    totalSizeMb: number;
    pendingCount: number;
    completedCount: number;
}

/** Orphaned files cleanup result - from RPC mark_orphaned_files_for_cleanup() */
export interface OrphanedFilesCleanupResult {
    filesMarked: number;
    totalSizeBytes: number;
    filePaths: string[];
}

/** Storage file integrity result - from RPC validate_storage_file_integrity() */
export interface StorageIntegrityResult {
    totalDbRecords: number;
    filesFound: number;
    filesMissing: number;
    missingFileIds: string[];
}

/** Storage health check item - from RPC get_storage_health_report() */
export interface StorageHealthCheckItem {
    checkName: string;
    status: 'OK' | 'WARNING' | 'CRITICAL' | 'INFO';
    details: Record<string, unknown>;
}

// ============================================
// Item 16: anonymous-uploads bucket specific interfaces
// ============================================

/** Anonymous uploads bucket statistics - from RPC get_anonymous_uploads_bucket_stats() */
export interface AnonymousUploadsBucketStats {
    bucketId: string;
    totalFiles: number;
    totalSizeBytes: number;
    totalSizeMb: number;
    avgFileSizeBytes: number;
    oldestFile: Date | null;
    newestFile: Date | null;
    filesByMonth: Array<{ month: string; count: number; size_mb: number }>;
    filesByCompany: Array<{ company_id: string; company_name: string; file_count: number; size_mb: number }>;
}

/** Orphaned file in anonymous-uploads - from RPC get_orphaned_anonymous_uploads_files() */
export interface OrphanedAnonymousUploadsFile {
    filePath: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    createdAt: Date;
    lastAccessedAt: Date | null;
    extractedCompanyId: string | null;
}

/** Anonymous uploads access log entry - from RPC get_anonymous_uploads_access_log() */
export interface AnonymousUploadsAccessLogEntry {
    filePath: string;
    uploadId: string | null;
    uploadEmail: string | null;
    companyId: string | null;
    companyName: string | null;
    accessCount: number;
    lastAccessedAt: Date | null;
    createdAt: Date;
    processingStatus: string | null;
}

/** Anonymous uploads company usage - from RPC get_anonymous_uploads_usage_by_company() */
export interface AnonymousUploadsCompanyUsage {
    companyId: string;
    companyName: string;
    totalFiles: number;
    totalSizeBytes: number;
    totalSizeMb: number;
    pendingCount: number;
    completedCount: number;
    documentsFolderCount: number;
    uploadsFolderCount: number;
}

// ============================================
// Item 26: Storage → Database Integration interfaces
// ============================================

/** Storage → Database sync health metrics - from RPC get_storage_database_sync_health() */
export interface StorageDatabaseSyncHealth {
    metricName: string;
    metricValue: number;
    description: string;
}

/** Incomplete upload record - from RPC get_incomplete_uploads() */
export interface IncompleteUpload {
    uploadId: string;
    filePath: string | null;
    userEmail: string | null;
    processingStatus: string;
    paymentStatus: string;
    createdAt: string;
    ageMinutes: number;
    correlationId: string | null;
}

/** Upload storage verification - from RPC verify_upload_storage_exists() */
export interface UploadStorageVerification {
    uploadId: string;
    filePath: string | null;
    dbRecordExists: boolean;
    expectedBucket: string;
    fileSize: number | null;
    createdAt: string | null;
    processingStatus: string | null;
    paymentStatus: string | null;
}

/** Manual review action result - from RPC mark_upload_for_manual_review() */
export interface ManualReviewResult {
    success: boolean;
    message: string;
    uploadId: string;
}

/** Upload pending manual review - from RPC get_uploads_pending_manual_review() */
export interface ManualReviewUpload {
    uploadId: string;
    filePath: string | null;
    userEmail: string | null;
    processingStatus: string;
    paymentStatus: string;
    createdAt: string;
    reviewReason: string | null;
    reviewRequestedAt: string | null;
}

// ============================================
// ITEM 27: WEBHOOK MONITORING INTERFACES (Stripe → Webhook integration)
// ============================================

/** Webhook health statistics - from RPC get_webhook_health_stats() */
export interface WebhookHealthStats {
    totalEvents: number;
    successfulEvents: number;
    failedEvents: number;
    signatureFailures: number;
    skippedEvents: number;
    successRate: number;
    avgDeliveryLatencyMs: number;
    avgProcessingLatencyMs: number;
    avgTotalLatencyMs: number;
    p50DeliveryLatencyMs: number;
    p95DeliveryLatencyMs: number;
    p99DeliveryLatencyMs: number;
    eventsByType: Record<string, number>;
    lastEventAt: string | null;
}

/** Recent webhook event - from RPC get_recent_webhook_events() */
export interface RecentWebhookEvent {
    id: string;
    stripeEventId: string;
    stripeEventType: string;
    stripeLivemode: boolean;
    receivedAt: string;
    processingStatus: 'received' | 'processing' | 'completed' | 'failed' | 'skipped';
    signatureValid: boolean;
    deliveryLatencyMs: number | null;
    processingLatencyMs: number | null;
    uploadId: string | null;
    correlationId: string;
    errorMessage: string | null;
}

/** Signature validation failure - from RPC get_signature_validation_failures() */
export interface SignatureValidationFailure {
    id: string;
    stripeEventId: string;
    stripeEventType: string;
    receivedAt: string;
    signatureError: string | null;
    correlationId: string;
    requestHeaders: Record<string, string> | null;
}

/** Webhook latency trend - from RPC get_webhook_latency_trends() */
export interface WebhookLatencyTrend {
    hourBucket: string;
    eventCount: number;
    avgDeliveryLatencyMs: number;
    avgProcessingLatencyMs: number;
    p95DeliveryLatencyMs: number;
    successRate: number;
}

/** Webhook configuration health check - from RPC get_webhook_configuration_health() */
export interface WebhookConfigHealthCheck {
    checkName: string;
    status: 'healthy' | 'warning' | 'critical';
    message: string;
    details: Record<string, unknown>;
}

export const dataService = {
    /**
     * Fetch company stats for the dashboard header (Lightweight)
     * Uses Promise.all to batch independent queries for better performance
     */
    getCompanyStats: async (companyId: string | null) => {
        try {
            // SUPER_ADMIN aggregated view: companyId is null
            if (!companyId) {
                const [companyCountResult, complexCountResult, complexesResult] = await Promise.all([
                    supabase
                        .from("companies")
                        .select("*", { count: 'exact', head: true }),
                    supabase
                        .from("complexes")
                        .select("*", { count: 'exact', head: true }),
                    supabase
                        .from('complexes')
                        .select('id')
                ]);

                if (companyCountResult.error) throw companyCountResult.error;
                if (complexCountResult.error) throw complexCountResult.error;
                if (complexesResult.error) throw complexesResult.error;

                const complexIds = complexesResult.data.map(c => c.id);
                let totalWeeklyCustomers = 0;

                if (complexIds.length > 0) {
                    const oneWeekAgo = new Date(Date.now() - TIME_MS.ONE_WEEK);
                    const { count: weeklyCount, error: uploadsError } = await supabase
                        .from('anonymous_uploads')
                        .select('*', { count: 'exact', head: true })
                        .in('complex_id', complexIds)
                        .eq('processing_status', 'completed')
                        .eq('payment_status', 'paid')
                        .gte('created_at', oneWeekAgo.toISOString());

                    if (uploadsError) throw uploadsError;
                    totalWeeklyCustomers = weeklyCount || 0;
                }

                return {
                    companyName: 'All Companies',
                    activeComplexes: complexCountResult.count || 0,
                    totalRevenue: 0,
                    totalWeeklyCustomers
                };
            }

            // Company-specific view
            // Batch independent queries: company details, complex count, and complex IDs
            const [companyResult, complexCountResult, complexesResult] = await Promise.all([
                // 1. Get Company Details
                supabase
                    .from("companies")
                    .select("*")
                    .eq("id", companyId)
                    .single(),
                // 2. Get Active Complexes Count
                supabase
                    .from("complexes")
                    .select("*", { count: 'exact', head: true })
                    .eq("company_id", companyId),
                // 3. Get Complex IDs for upload query
                supabase
                    .from('complexes')
                    .select('id')
                    .eq('company_id', companyId)
            ]);

            if (companyResult.error) throw companyResult.error;
            if (complexCountResult.error) throw complexCountResult.error;
            if (complexesResult.error) throw complexesResult.error;

            const company = companyResult.data;
            const complexCount = complexCountResult.count;
            const complexIds = complexesResult.data.map(c => c.id);

            // 4. Get weekly upload count (depends on complexIds from query 3)
            let totalWeeklyCustomers = 0;

            if (complexIds.length > 0) {
                const oneWeekAgo = new Date(Date.now() - TIME_MS.ONE_WEEK);

                const { count: weeklyCount, error: uploadsError } = await supabase
                    .from('anonymous_uploads')
                    .select('*', { count: 'exact', head: true })
                    .in('complex_id', complexIds)
                    .eq('processing_status', 'completed')
                    .eq('payment_status', 'paid')
                    .gte('created_at', oneWeekAgo.toISOString());

                if (uploadsError) throw uploadsError;
                totalWeeklyCustomers = weeklyCount || 0;
            }

            // Revenue is calculated in the UI as totalWeeklyCustomers * ESA_LETTER_PRICE
            // We return 0 for totalRevenue here because the UI will calculate it.

            return {
                companyName: company.name,

                activeComplexes: complexCount || 0,
                totalRevenue: 0, // Deprecated in favor of UI calculation
                totalWeeklyCustomers
            };

        } catch (error: unknown) {
            logger.error("Error fetching company stats", error);
            throw error;
        }
    },

    /**
     * Fetch daily stats for Verified ESA Letters
     */
    getVerifiedESALettersStats: async (companyId: string | null, days: number | 'all' = 30) => {
        try {
            // Use millisecond-based calculation for accurate date offsets
            const startDate: Date | null = days === 'all'
                ? null // No lower bound
                : new Date(Date.now() - days * TIME_MS.ONE_DAY);

            // SUPER_ADMIN aggregated view: query all completed paid uploads
            if (!companyId) {
                let query = supabase
                    .from('anonymous_uploads')
                    .select('created_at')
                    .eq('processing_status', 'completed')
                    .eq('payment_status', 'paid');

                if (startDate) {
                    query = query.gte('created_at', startDate.toISOString());
                }

                const { data: uploads, error: uploadsError } = await query;
                if (uploadsError) throw uploadsError;

                const dailyMap = new Map<string, number>();
                if (typeof days === 'number') {
                    const now = Date.now();
                    for (let i = 0; i <= days; i++) {
                        const d = new Date(now - (days - i) * TIME_MS.ONE_DAY);
                        dailyMap.set(d.toISOString().split('T')[0], 0);
                    }
                }

                uploads?.forEach(upload => {
                    const date = (upload.created_at || '').split('T')[0];
                    dailyMap.set(date, (dailyMap.get(date) || 0) + 1);
                });

                return Array.from(dailyMap.entries())
                    .map(([date, count]) => ({ date, count }))
                    .sort((a, b) => a.date.localeCompare(b.date));
            }

            // Company-specific view
            // Get all complexes for this company first
            const { data: complexes, error: complexError } = await supabase
                .from('complexes')
                .select('id')
                .eq('company_id', companyId);

            if (complexError) throw complexError;

            const complexIds = complexes.map(c => c.id);

            if (complexIds.length === 0) return [];

            // Fetch completed uploads for these complexes
            let query = supabase
                .from('anonymous_uploads')
                .select('created_at')
                .in('complex_id', complexIds)
                .eq('processing_status', 'completed')
                .eq('payment_status', 'paid');

            if (startDate) {
                query = query.gte('created_at', startDate.toISOString());
            }

            const { data: uploads, error: uploadsError } = await query;

            if (uploadsError) throw uploadsError;

            // Aggregate by date
            const dailyMap = new Map<string, number>();

            // Initialize days with 0 if not 'all' (to show empty days in chart)
            if (typeof days === 'number') {
                const now = Date.now();
                for (let i = 0; i <= days; i++) {
                    const d = new Date(now - (days - i) * TIME_MS.ONE_DAY);
                    dailyMap.set(d.toISOString().split('T')[0], 0);
                }
            }

            uploads?.forEach(upload => {
                const date = (upload.created_at || '').split('T')[0];
                dailyMap.set(date, (dailyMap.get(date) || 0) + 1);
            });

            return Array.from(dailyMap.entries())
                .map(([date, count]) => ({ date, count }))
                .sort((a, b) => a.date.localeCompare(b.date));

        } catch (error: unknown) {
            logger.error("Error fetching verified letter stats", error);
            return [];
        }
    },

    /**
     * Fetch complexes with server-side pagination
     */
    getComplexesPaginated: async (companyId: string | null, page: number = 1, limit: number = 5, assignedComplexIds?: string[], cursor?: string): Promise<{ complexes: ApartmentComplex[], total: number }> => {
        try {
            let query = supabase
                .from("complexes")
                .select("*, customers(*)", { count: 'exact' });

            // Filter by company when companyId is provided (non-SUPER_ADMIN)
            if (companyId) {
                query = query.eq("company_id", companyId);
            }

            // Apply role-based filtering if provided
            if (assignedComplexIds && assignedComplexIds.length > 0) {
                query = query.in('id', assignedComplexIds);
            }

            // Cursor-based pagination: use .gt('created_at', cursor) for pages beyond the first.
            // For page 1 (no cursor), just order + limit.
            // This avoids OFFSET-based .range() which degrades on deep pages.
            if (page > 1) {
                const paginationCursor = cursor || new Date().toISOString();
                query = query.gt('created_at', paginationCursor);
            }

            const { data: complexesData, count, error } = await query
                .order('created_at', { ascending: false })
                .limit(limit);

            if (error) throw error;

            const complexes: ApartmentComplex[] = [];

            if (complexesData && complexesData.length > 0) {
                const complexIds = complexesData.map((c: DatabaseComplex) => c.id);

                // Fetch uploads for ALL these complexes in one go
                // Select only columns needed for customer transformation (not llm_result or metadata)
                const { data: uploadsData } = await supabase
                    .from('anonymous_uploads')
                    .select('id, user_name, user_email, processing_status, payment_status, payment_amount, created_at, complex_id, authenticity_score')
                    .in('complex_id', complexIds)
                    .eq('payment_status', 'paid')
                    .in('processing_status', ['completed', 'processing'])
                    .order('created_at', { ascending: false })
                    .limit(100);

                // Map uploads by complex ID
                const uploadsByComplex = new Map<string, AnonymousUpload[]>();
                if (uploadsData) {
                    (uploadsData as unknown as DatabaseAnonymousUpload[]).forEach((u) => {
                        const upload = transformDatabaseAnonymousUpload(u);
                        const cid = u.complex_id;
                        if (cid && !uploadsByComplex.has(cid)) {
                            uploadsByComplex.set(cid, []);
                        }
                        if (cid) uploadsByComplex.get(cid)?.push(upload);
                    });
                }

                for (const complex of complexesData as DatabaseComplex[]) {
                    const uploads = uploadsByComplex.get(complex.id) || [];
                    const customers = transformUploadsToCustomers(uploads);

                    // Sort by date submitted (descending)
                    customers.sort((a, b) => new Date(b.dateSubmitted).getTime() - new Date(a.dateSubmitted).getTime());

                    complexes.push(transformDatabaseComplex(complex as DatabaseComplex, customers));
                }
            }

            return {
                complexes,
                total: count || 0
            };

        } catch (error: unknown) {
            logger.error("Error fetching paginated complexes", error);

            throw error;
        }
    },

    /**
     * Fetch all complexes for a company
     */
    getComplexes: async (companyId: string): Promise<ApartmentComplex[]> => {
        try {
            const { data: complexesData, error } = await supabase
                .from("complexes")
                .select("id, name, address, company_id, created_at")
                .eq("company_id", companyId);

            if (error || !complexesData) {
                throw new Error(`Failed to fetch complexes: ${error?.message}`);
            }

            const complexes: ApartmentComplex[] = [];

            if (complexesData.length > 0) {
                const complexIds = complexesData.map((c: DatabaseComplex) => c.id);

                // Fetch uploads for ALL these complexes in one go
                const { data: uploadsData } = await supabase
                    .from('anonymous_uploads')
                    .select('*')
                    .in('complex_id', complexIds)
                    .eq('payment_status', 'paid')
                    .in('processing_status', ['completed', 'processing'])
                    .order('created_at', { ascending: false });

                // Map uploads by complex ID
                const uploadsByComplex = new Map<string, AnonymousUpload[]>();
                if (uploadsData) {
                    (uploadsData as unknown as DatabaseAnonymousUpload[]).forEach((u) => {
                        const upload = transformDatabaseAnonymousUpload(u);
                        const cid = u.complex_id;
                        if (cid && !uploadsByComplex.has(cid)) {
                            uploadsByComplex.set(cid, []);
                        }
                        if (cid) uploadsByComplex.get(cid)?.push(upload);
                    });
                }

                for (const complex of complexesData as DatabaseComplex[]) {
                    const uploads = uploadsByComplex.get(complex.id) || [];
                    const customers = transformUploadsToCustomers(uploads);

                    // Sort by date submitted (descending)
                    customers.sort((a, b) => new Date(b.dateSubmitted).getTime() - new Date(a.dateSubmitted).getTime());

                    complexes.push(transformDatabaseComplex(complex as DatabaseComplex, customers));
                }
            }

            return complexes;
        } catch (error: unknown) {
            logger.error("Error fetching complexes", error);
            throw error;
        }
    },

    /**
     * Fetch customers for a specific complex
     */
    getComplexCustomers: async (complexId: string): Promise<Customer[]> => {
        try {
            const { data: uploadsData, error } = await supabase
                .from('anonymous_uploads')
                .select('*')
                .eq('complex_id', complexId)
                .eq('payment_status', 'paid')
                .in('processing_status', ['completed', 'processing'])
                .order('created_at', { ascending: false });

            if (error) {
                throw new Error(`Failed to fetch uploads: ${error.message}`);
            }

            if (!uploadsData) {
                return [];
            }

            const uploads = uploadsData.map(transformDatabaseAnonymousUpload);
            return transformUploadsToCustomers(uploads);
        } catch (error: unknown) {
            logger.error("Error fetching complex customers", error);
            throw error;
        }
    },

    /**
     * Fetch all users in a company
     */
    getUsers: async (companyId: string): Promise<User[]> => {
        try {
            const { data: usersData, error } = await supabase
                .from("users")
                .select("*")
                .eq("company_id", companyId);

            if (error || !usersData) {
                throw new Error(`Failed to fetch users: ${error?.message}`);
            }

            return transformDatabaseUsers(usersData as DatabaseUser[]);
        } catch (error: unknown) {
            logger.error("Error fetching users", error);
            throw error;
        }
    },

    /**
     * Fetch ALL users across all companies (SUPER_ADMIN only)
     */
    getAllUsers: async (): Promise<User[]> => {
        try {
            const { data: usersData, error } = await supabase
                .from("users")
                .select("*")
                .order("created_at", { ascending: false });

            if (error || !usersData) {
                throw new Error(`Failed to fetch all users: ${error?.message}`);
            }

            return transformDatabaseUsers(usersData as DatabaseUser[]);
        } catch (error: unknown) {
            logger.error("Error fetching all users", error);
            throw error;
        }
    },

    /**
     * Fetch a single user by ID
     */
    getUserById: async (userId: string): Promise<User> => {
        try {
            const { data: userData, error } = await supabase
                .from("users")
                .select("*")
                .eq("id", userId)
                .single();

            if (error || !userData) {
                throw new Error(`Failed to fetch user: ${error?.message}`);
            }

            return transformDatabaseUser(userData as DatabaseUser);
        } catch (error: unknown) {
            logger.error("Error fetching user", error);
            throw error;
        }
    },

    /**
     * Fetch a single user by Email
     */
    getUserByEmail: async (email: string): Promise<User | null> => {
        try {
            const { data: userData, error } = await supabase
                .from("users")
                .select("*")
                .eq("email", email)
                .single();

            if (error) {
                if (error.code === 'PGRST116') return null; // Not found
                throw new Error(`Failed to fetch user by email: ${error.message}`);
            }

            return transformDatabaseUser(userData as DatabaseUser);
        } catch (error: unknown) {
            logger.error("Error fetching user by email", error);
            return null;
        }
    },
    /**
     * Fetch all companies (for super admin)
     */
    getAllCompanies: async (): Promise<Partial<PropertyManagementCompany>[]> => {
        try {
            const { data, error } = await supabase
                .from("companies")
                .select("*")
                .order("name");

            if (error) throw error;

            return data.map((company) => ({
                id: company.id,
                name: company.name,
                phoneNumber: company.phone_number || '',
                complexes: [],
                users: []
            }));
        } catch (error: unknown) {
            logger.error("Error fetching all companies", error);
            throw error;
        }
    },
    /**
     * Create a new company
     */
    createCompany: async (name: string, _ownerName: string, _email: string): Promise<PropertyManagementCompany> => {
        try {
            const { data, error } = await supabase
                .from("companies")
                .insert({
                    name,
                    phone_number: '', // Optional or can be added later
                })
                .select()
                .single();

            if (error) throw error;

            return transformDatabaseCompany(
                data as DatabaseCompany,
                [],
                []
            );
        } catch (error: unknown) {
            logger.error("Error creating company", error);
            throw error;
        }
    },

    /**
     * Fetch a single upload by ID
     */
    getUploadById: async (uploadId: string): Promise<AnonymousUpload> => {
        try {
            const { data, error } = await supabase
                .from("anonymous_uploads")
                .select("*")
                .eq("id", uploadId)
                .single();

            if (error || !data) {
                throw new Error(`Failed to fetch upload: ${error?.message}`);
            }

            return transformDatabaseAnonymousUpload(data as DatabaseAnonymousUpload);
        } catch (error: unknown) {
            logger.error("Error fetching upload", error);
            throw error;
        }
    },

    /**
     * Fetch system-wide admin stats (for super admin dashboard)
     * Uses Promise.all to batch all 6 independent queries for better performance
     */
    getAdminStats: async () => {
        try {
            // Calculate date boundaries using immutable date creation
            const now = new Date();
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

            const oneWeekAgo = new Date(Date.now() - TIME_MS.ONE_WEEK);

            const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

            // Batch all 6 independent queries
            const [
                companiesResult,
                usersResult,
                documentsResult,
                newCompaniesResult,
                newUsersResult,
                newDocsResult
            ] = await Promise.all([
                // 1. Total companies count
                supabase
                    .from("companies")
                    .select("*", { count: 'exact', head: true }),
                // 2. Total users count
                supabase
                    .from("users")
                    .select("*", { count: 'exact', head: true }),
                // 3. Active documents count (paid and completed)
                supabase
                    .from("anonymous_uploads")
                    .select("*", { count: 'exact', head: true })
                    .eq("payment_status", "paid")
                    .eq("processing_status", "completed"),
                // 4. Companies added this month
                supabase
                    .from("companies")
                    .select("*", { count: 'exact', head: true })
                    .gte("created_at", startOfMonth.toISOString()),
                // 5. Users added this week
                supabase
                    .from("users")
                    .select("*", { count: 'exact', head: true })
                    .gte("created_at", oneWeekAgo.toISOString()),
                // 6. Documents added today
                supabase
                    .from("anonymous_uploads")
                    .select("*", { count: 'exact', head: true })
                    .eq("payment_status", "paid")
                    .gte("created_at", startOfToday.toISOString())
            ]);

            // Check for errors
            if (companiesResult.error) throw companiesResult.error;
            if (usersResult.error) throw usersResult.error;
            if (documentsResult.error) throw documentsResult.error;
            if (newCompaniesResult.error) throw newCompaniesResult.error;
            if (newUsersResult.error) throw newUsersResult.error;
            if (newDocsResult.error) throw newDocsResult.error;

            return {
                totalCompanies: companiesResult.count || 0,
                totalUsers: usersResult.count || 0,
                activeDocuments: documentsResult.count || 0,
                newCompaniesThisMonth: newCompaniesResult.count || 0,
                newUsersThisWeek: newUsersResult.count || 0,
                newDocsToday: newDocsResult.count || 0,
            };
        } catch (error: unknown) {
            logger.error("Error fetching admin stats", error);
            throw error;
        }
    },

    // ============================================
    // USER METHODS (Item 17 - dataService optimization)
    // ============================================

    /**
     * Fetch users with server-side pagination
     * Uses retry logic for transient failures
     */
    getUsersPaginated: async (
        companyId: string,
        limit: number = 20,
        offset: number = 0
    ): Promise<PaginatedUsersResult> => {
        return withRetry(
            async () => {
                const { data, count, error } = await supabase
                    .from("users")
                    .select("*", { count: 'exact' })
                    .eq("company_id", companyId)
                    .order("name", { ascending: true })
                    .range(offset, offset + limit - 1);

                if (error) throw error;

                const users = transformDatabaseUsers((data || []) as DatabaseUser[]);

                return {
                    users,
                    total: count || 0,
                    limit,
                    offset,
                    hasMore: (offset + limit) < (count || 0),
                };
            },
            'dataService.getUsersPaginated',
            { maxAttempts: 3 }
        );
    },

    /**
     * Search users by name or email
     * Uses ILIKE for case-insensitive matching
     */
    searchUsers: async (
        companyId: string,
        searchTerm: string,
        limit: number = 20
    ): Promise<User[]> => {
        return withRetry(
            async () => {
                if (!searchTerm.trim()) {
                    return [];
                }

                const term = `%${searchTerm.trim()}%`;

                const { data, error } = await supabase
                    .from("users")
                    .select("*")
                    .eq("company_id", companyId)
                    .or(`name.ilike.${term},email.ilike.${term}`)
                    .order("name", { ascending: true })
                    .limit(limit);

                if (error) throw error;

                return transformDatabaseUsers((data || []) as DatabaseUser[]);
            },
            'dataService.searchUsers',
            { maxAttempts: 2 }
        );
    },

    /**
     * Fetch users by role
     * Optimized for role-based filtering
     */
    getUsersByRole: async (
        companyId: string,
        role: UserRole,
        limit?: number
    ): Promise<User[]> => {
        return withRetry(
            async () => {
                let query = supabase
                    .from("users")
                    .select("*")
                    .eq("company_id", companyId)
                    .eq("role", role)
                    .order("name", { ascending: true });

                if (limit) {
                    query = query.limit(limit);
                }

                const { data, error } = await query;

                if (error) throw error;

                return transformDatabaseUsers((data || []) as DatabaseUser[]);
            },
            'dataService.getUsersByRole',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get user statistics for a company
     * Uses Promise.all for parallel queries
     */
    getUserStatistics: async (companyId: string): Promise<UserStatistics> => {
        return withRetry(
            async () => {
                const oneWeekAgo = new Date(Date.now() - TIME_MS.ONE_WEEK);
                const startOfMonth = new Date();
                startOfMonth.setDate(1);
                startOfMonth.setHours(0, 0, 0, 0);

                // Batch all independent queries
                const [
                    totalResult,
                    ownerResult,
                    regionalResult,
                    propertyResult,
                    weeklyResult,
                    monthlyResult,
                    allUsersResult,
                ] = await Promise.all([
                    // 1. Total users count
                    supabase
                        .from("users")
                        .select("*", { count: 'exact', head: true })
                        .eq("company_id", companyId),
                    // 2. Owners count
                    supabase
                        .from("users")
                        .select("*", { count: 'exact', head: true })
                        .eq("company_id", companyId)
                        .eq("role", "COMPANY_OWNER"),
                    // 3. Regional managers count
                    supabase
                        .from("users")
                        .select("*", { count: 'exact', head: true })
                        .eq("company_id", companyId)
                        .eq("role", "REGIONAL_MANAGER"),
                    // 4. Property managers count
                    supabase
                        .from("users")
                        .select("*", { count: 'exact', head: true })
                        .eq("company_id", companyId)
                        .eq("role", "PROPERTY_MANAGER"),
                    // 5. Users added this week
                    supabase
                        .from("users")
                        .select("*", { count: 'exact', head: true })
                        .eq("company_id", companyId)
                        .gte("created_at", oneWeekAgo.toISOString()),
                    // 6. Users added this month
                    supabase
                        .from("users")
                        .select("*", { count: 'exact', head: true })
                        .eq("company_id", companyId)
                        .gte("created_at", startOfMonth.toISOString()),
                    // 7. Total complex assignments for average calculation (from junction table)
                    supabase
                        .from("user_complex_assignments")
                        .select("user_id, complex_id", { count: 'exact', head: true }),
                ]);

                // Check for errors
                if (totalResult.error) throw totalResult.error;
                if (ownerResult.error) throw ownerResult.error;
                if (regionalResult.error) throw regionalResult.error;
                if (propertyResult.error) throw propertyResult.error;
                if (weeklyResult.error) throw weeklyResult.error;
                if (monthlyResult.error) throw monthlyResult.error;
                if (allUsersResult.error) throw allUsersResult.error;

                // Calculate average complexes per user (from junction table count)
                const totalComplexAssignments = allUsersResult.count || 0;
                const totalUsers = totalResult.count || 0;
                const averageComplexesPerUser = totalUsers > 0
                    ? Math.round((totalComplexAssignments / totalUsers) * 10) / 10
                    : 0;

                return {
                    totalUsers,
                    usersByRole: {
                        SUPER_ADMIN: 0, // Super admins are not company-scoped
                        COMPANY_OWNER: ownerResult.count || 0,
                        REGIONAL_MANAGER: regionalResult.count || 0,
                        PROPERTY_MANAGER: propertyResult.count || 0,
                    },
                    usersAddedThisWeek: weeklyResult.count || 0,
                    usersAddedThisMonth: monthlyResult.count || 0,
                    averageComplexesPerUser,
                };
            },
            'dataService.getUserStatistics',
            { maxAttempts: 2 }
        );
    },

    /**
     * Update user role
     * Validates role before update
     */
    updateUserRole: async (
        userId: string,
        role: UserRole
    ): Promise<User> => {
        return withRetry(
            async () => {
                // Validate role
                const validRoles: UserRole[] = ['COMPANY_OWNER', 'REGIONAL_MANAGER', 'PROPERTY_MANAGER'];
                if (!validRoles.includes(role)) {
                    throw new Error(`Invalid role: ${role}. Must be one of: ${validRoles.join(', ')}`);
                }

                const { data, error } = await supabase
                    .from("users")
                    .update({ role, updated_at: new Date().toISOString() })
                    .eq("id", userId)
                    .select()
                    .single();

                if (error) throw error;
                if (!data) throw new Error('User not found');

                return transformDatabaseUser(data as DatabaseUser);
            },
            'dataService.updateUserRole',
            { maxAttempts: 2 }
        );
    },

    /**
     * Update user assigned complexes
     * Validates complex IDs before update
     */
    updateUserAssignedComplexes: async (
        userId: string,
        assignedComplexIds: string[]
    ): Promise<User> => {
        return withRetry(
            async () => {
                // Validate UUID format for all IDs
                const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                const invalidIds = assignedComplexIds.filter(id => !uuidRegex.test(id));
                if (invalidIds.length > 0) {
                    throw new Error(`Invalid complex IDs: ${invalidIds.join(', ')}`);
                }

                // Use junction table for assignments (source of truth)
                // 1. Get current assignments
                const { data: currentAssignments } = await supabase
                    .from('user_complex_assignments')
                    .select('complex_id')
                    .eq('user_id', userId);

                const currentSet = new Set(currentAssignments?.map(a => a.complex_id) || []);
                const newSet = new Set(assignedComplexIds);

                // 2. Add new assignments
                const toAdd = [...newSet].filter(id => !currentSet.has(id));
                if (toAdd.length > 0) {
                    const { error: addError } = await supabase
                        .from('user_complex_assignments')
                        .insert(toAdd.map(complexId => ({ user_id: userId, complex_id: complexId })));
                    if (addError) throw addError;
                }

                // 3. Remove old assignments
                const toRemove = [...currentSet].filter(id => !newSet.has(id));
                if (toRemove.length > 0) {
                    const { error: removeError } = await supabase
                        .from('user_complex_assignments')
                        .delete()
                        .eq('user_id', userId)
                        .in('complex_id', toRemove);
                    if (removeError) throw removeError;
                }

                // 4. Fetch user record
                const { data, error } = await supabase
                    .from("users")
                    .select()
                    .eq("id", userId)
                    .single();

                if (error) throw error;
                if (!data) throw new Error('User not found');

                const user = transformDatabaseUser(data as DatabaseUser);
                user.assignedComplexIds = assignedComplexIds;
                user.assignedComplexCount = assignedComplexIds.length;
                return user;
            },
            'dataService.updateUserAssignedComplexes',
            { maxAttempts: 2 }
        );
    },

    /**
     * Create a new user
     * Uses retry logic for transient failures
     */
    createUser: async (
        name: string,
        email: string,
        role: UserRole,
        companyId: string,
        assignedComplexIds: string[] = []
    ): Promise<User> => {
        return withRetry(
            async () => {
                // Validate email format
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(email)) {
                    throw new Error('Invalid email format');
                }

                // Validate role
                const validRoles: UserRole[] = ['COMPANY_OWNER', 'REGIONAL_MANAGER', 'PROPERTY_MANAGER'];
                if (!validRoles.includes(role)) {
                    throw new Error(`Invalid role: ${role}. Must be one of: ${validRoles.join(', ')}`);
                }

                const { data, error } = await supabase
                    .from("users")
                    .insert({
                        name,
                        email: email.toLowerCase().trim(),
                        role,
                        company_id: companyId,
                    })
                    .select()
                    .single();

                if (error) {
                    // Check for unique constraint violation
                    if (error.code === '23505' && error.message.includes('email')) {
                        throw new Error('A user with this email already exists');
                    }
                    throw error;
                }

                // Create junction table assignments if provided
                if (assignedComplexIds.length > 0) {
                    const { error: assignError } = await supabase
                        .from('user_complex_assignments')
                        .insert(assignedComplexIds.map(complexId => ({
                            user_id: data.id,
                            complex_id: complexId,
                        })));
                    if (assignError) {
                        logger.warn('Failed to create complex assignments for new user', {
                            userId: data.id,
                            error: assignError.message,
                        });
                    }
                }

                const user = transformDatabaseUser(data as DatabaseUser);
                user.assignedComplexIds = assignedComplexIds;
                user.assignedComplexCount = assignedComplexIds.length;
                return user;
            },
            'dataService.createUser',
            { maxAttempts: 2 }
        );
    },

    /**
     * Delete a user
     * Returns true if successful
     */
    deleteUser: async (userId: string): Promise<boolean> => {
        return withRetry(
            async () => {
                const { error } = await supabase
                    .from("users")
                    .delete()
                    .eq("id", userId);

                if (error) throw error;

                return true;
            },
            'dataService.deleteUser',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get users with assigned complex count
     * Optimized lightweight query for lists
     */
    getUsersSummary: async (companyId: string): Promise<Array<{
        id: string;
        name: string;
        email: string;
        role: UserRole;
        assignedComplexCount: number;
    }>> => {
        return withRetry(
            async () => {
                // Fetch users and their assignment counts from junction table
                const [usersResult, assignmentsResult] = await Promise.all([
                    supabase
                        .from("users")
                        .select("id, name, email, role")
                        .eq("company_id", companyId)
                        .order("name", { ascending: true }),
                    supabase
                        .from("user_complex_assignments")
                        .select("user_id"),
                ]);

                if (usersResult.error) throw usersResult.error;

                // Count assignments per user
                const assignmentCounts = new Map<string, number>();
                for (const assignment of (assignmentsResult.data || [])) {
                    assignmentCounts.set(
                        assignment.user_id,
                        (assignmentCounts.get(assignment.user_id) || 0) + 1
                    );
                }

                return (usersResult.data || []).map(user => ({
                    id: user.id,
                    name: user.name || '',
                    email: user.email || '',
                    role: (user.role || 'PROPERTY_MANAGER') as UserRole,
                    assignedComplexCount: assignmentCounts.get(user.id) || 0,
                }));
            },
            'dataService.getUsersSummary',
            { maxAttempts: 2 }
        );
    },

    // ============================================
    // COMPANY RPC FUNCTIONS (Item 9 - companies table optimization)
    // ============================================

    /**
     * Get platform-wide company statistics (SUPER_ADMIN only)
     * Uses RPC function: get_company_statistics()
     */
    getCompanyStatistics: async (): Promise<CompanyStatistics> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_company_statistics');

                if (error) throw error;
                if (!data || data.length === 0) {
                    return {
                        totalCompanies: 0,
                        totalComplexes: 0,
                        totalUsers: 0,
                        companiesThisMonth: 0,
                        avgComplexesPerCompany: 0,
                        avgUsersPerCompany: 0,
                    };
                }

                const row = data[0];
                return {
                    totalCompanies: Number(row.total_companies) || 0,
                    totalComplexes: Number(row.total_complexes) || 0,
                    totalUsers: Number(row.total_users) || 0,
                    companiesThisMonth: Number(row.companies_this_month) || 0,
                    avgComplexesPerCompany: Number(row.avg_complexes_per_company) || 0,
                    avgUsersPerCompany: Number(row.avg_users_per_company) || 0,
                };
            },
            'dataService.getCompanyStatistics',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get detailed statistics for a specific company
     * Uses RPC function: get_company_detail_statistics(p_company_id)
     */
    getCompanyDetailStatistics: async (companyId: string): Promise<CompanyDetailStatistics> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_company_detail_statistics', {
                    p_company_id: companyId,
                });

                if (error) throw error;
                if (!data || data.length === 0) {
                    throw new Error('Company not found');
                }

                const row = data[0];
                return {
                    companyName: row.company_name || '',
                    totalComplexes: Number(row.total_complexes) || 0,
                    totalUsers: Number(row.total_users) || 0,
                    totalUploads: Number(row.total_uploads) || 0,
                    completedUploads: Number(row.completed_uploads) || 0,
                    pendingUploads: Number(row.pending_uploads) || 0,
                    totalRevenue: Number(row.total_revenue) || 0,
                };
            },
            'dataService.getCompanyDetailStatistics',
            { maxAttempts: 2 }
        );
    },

    /**
     * Search companies by name with fuzzy matching
     * Uses RPC function: search_companies(p_search_term, p_limit)
     */
    searchCompanies: async (searchTerm: string, limit: number = 10): Promise<CompanySearchResult[]> => {
        return withRetry(
            async () => {
                if (!searchTerm || searchTerm.trim().length < 2) {
                    return [];
                }

                const { data, error } = await rpc('search_companies', {
                    p_search_term: searchTerm.trim(),
                    p_limit: limit,
                });

                if (error) throw error;

                return (data || []).map((row: { id: string; name: string; owner_name: string; owner_email: string | null; similarity_score: number }) => ({
                    id: row.id,
                    name: row.name,
                    
                    ownerEmail: row.owner_email,
                    similarityScore: row.similarity_score,
                }));
            },
            'dataService.searchCompanies',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get companies with pagination (SUPER_ADMIN only)
     * Uses RPC function: get_companies_paginated(p_page, p_page_size, p_sort_by, p_sort_order)
     */
    getCompaniesPaginated: async (
        page: number = 1,
        pageSize: number = 20,
        sortBy: 'name' | 'owner_name' | 'created_at' = 'created_at',
        sortOrder: 'asc' | 'desc' = 'desc'
    ): Promise<PaginatedCompaniesResult> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_companies_paginated', {
                    p_page: page,
                    p_page_size: pageSize,
                    p_sort_by: sortBy,
                    p_sort_order: sortOrder,
                });

                if (error) throw error;
                if (!data || data.length === 0) {
                    return {
                        companies: [],
                        totalCount: 0,
                        page: page,
                        pageSize: pageSize,
                        totalPages: 0,
                    };
                }

                const row = data[0];
                // Transform companies with empty arrays for complexes/users (not needed for list view)
                const companies = (row.companies || []).map((c: DatabaseCompany) => transformDatabaseCompany(c, [], []));

                return {
                    companies,
                    totalCount: Number(row.total_count) || 0,
                    page: row.page,
                    pageSize: row.page_size,
                    totalPages: row.total_pages,
                };
            },
            'dataService.getCompaniesPaginated',
            { maxAttempts: 2 }
        );
    },

    /**
     * Update a company
     */
    updateCompany: async (
        companyId: string,
        updates: { name?: string; ownerName?: string; ownerEmail?: string; phoneNumber?: string }
    ): Promise<PropertyManagementCompany> => {
        return withRetry(
            async () => {
                const dbUpdates: Partial<DatabaseCompany> = {};
                if (updates.name !== undefined) dbUpdates.name = updates.name;
                if (updates.phoneNumber !== undefined) dbUpdates.phone_number = updates.phoneNumber;

                const { data, error } = await supabase
                    .from('companies')
                    .update(dbUpdates)
                    .eq('id', companyId)
                    .select()
                    .single();

                if (error) throw error;
                // Transform with empty arrays for complexes/users (not needed for update response)
                return transformDatabaseCompany(data, [], []);
            },
            'dataService.updateCompany',
            { maxAttempts: 2 }
        );
    },

    /**
     * Delete a company (SUPER_ADMIN only - cascades to users and complexes)
     */
    deleteCompany: async (companyId: string): Promise<void> => {
        return withRetry(
            async () => {
                const { error } = await supabase
                    .from('companies')
                    .delete()
                    .eq('id', companyId);

                if (error) throw error;
            },
            'dataService.deleteCompany',
            { maxAttempts: 2 }
        );
    },

    // ============================================
    // ITEM 10: RPC-based user methods (users table optimization)
    // These use the optimized database functions for better performance
    // ============================================

    /**
     * Get platform-wide user statistics (SUPER_ADMIN only)
     * Uses RPC function: get_platform_user_statistics()
     */
    getPlatformUserStatistics: async (): Promise<PlatformUserStatistics> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_platform_user_statistics');

                if (error) throw error;
                if (!data || data.length === 0) {
                    return {
                        totalUsers: 0,
                        activeUsers: 0,
                        inactiveUsers: 0,
                        usersByRole: {},
                        usersThisWeek: 0,
                        usersThisMonth: 0,
                        emailConfirmedCount: 0,
                        avgComplexesPerUser: 0,
                    };
                }

                const row = data[0];
                return {
                    totalUsers: Number(row.total_users) || 0,
                    activeUsers: Number(row.active_users) || 0,
                    inactiveUsers: Number(row.inactive_users) || 0,
                    usersByRole: row.users_by_role || {},
                    usersThisWeek: Number(row.users_this_week) || 0,
                    usersThisMonth: Number(row.users_this_month) || 0,
                    emailConfirmedCount: Number(row.email_confirmed_count) || 0,
                    avgComplexesPerUser: Number(row.avg_complexes_per_user) || 0,
                };
            },
            'dataService.getPlatformUserStatistics',
            { maxAttempts: 2 }
        );
    },

    /**
     * Search users with fuzzy matching using trigram similarity
     * Uses RPC function: search_users(p_search_term, p_company_id, p_limit)
     * More efficient than client-side ILIKE queries for fuzzy search
     */
    searchUsersRPC: async (
        searchTerm: string,
        companyId: string | null = null,
        limit: number = 20
    ): Promise<UserSearchResult[]> => {
        return withRetry(
            async () => {
                if (!searchTerm || searchTerm.trim().length < 2) {
                    return [];
                }

                const { data, error } = await rpc('search_users', {
                    p_search_term: searchTerm.trim(),
                    p_company_id: companyId,
                    p_limit: limit,
                });

                if (error) throw error;

                return (data || []).map((row: {
                    id: string;
                    name: string;
                    email: string;
                    role: string;
                    company_id: string | null;
                    is_active: boolean;
                    similarity_score: number;
                }) => ({
                    id: row.id,
                    name: row.name,
                    email: row.email,
                    role: row.role,
                    companyId: row.company_id,
                    isActive: row.is_active,
                    similarityScore: row.similarity_score,
                }));
            },
            'dataService.searchUsersRPC',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get paginated users with advanced filters
     * Uses RPC function: get_users_paginated(...)
     * Supports server-side pagination, filtering by role/active status, and sorting
     */
    getUsersPaginatedRPC: async (
        companyId: string | null = null,
        page: number = 1,
        pageSize: number = 20,
        role: string | null = null,
        isActive: boolean | null = null,
        sortBy: 'name' | 'email' | 'created_at' | 'last_login' = 'created_at',
        sortOrder: 'asc' | 'desc' = 'desc'
    ): Promise<RPCPaginatedUsersResult> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_users_paginated', {
                    p_company_id: companyId,
                    p_page: page,
                    p_page_size: pageSize,
                    p_role: role,
                    p_is_active: isActive,
                    p_sort_by: sortBy,
                    p_sort_order: sortOrder,
                });

                if (error) throw error;
                if (!data || data.length === 0) {
                    return {
                        users: [],
                        totalCount: 0,
                        page: page,
                        pageSize: pageSize,
                        totalPages: 0,
                    };
                }

                const row = data[0];
                // Transform the raw user objects from JSONB to User type
                const users = (row.users || []).map((u: DatabaseUser) => transformDatabaseUser(u));

                return {
                    users,
                    totalCount: Number(row.total_count) || 0,
                    page: row.page,
                    pageSize: row.page_size,
                    totalPages: row.total_pages,
                };
            },
            'dataService.getUsersPaginatedRPC',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get user activity summary for monitoring
     * Uses RPC function: get_user_activity_summary(p_company_id, p_days)
     * Provides insights into user login patterns and engagement
     */
    getUserActivitySummary: async (
        companyId: string | null = null,
        days: number = 30
    ): Promise<UserActivitySummary> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_user_activity_summary', {
                    p_company_id: companyId,
                    p_days: days,
                });

                if (error) throw error;
                if (!data || data.length === 0) {
                    return {
                        totalUsers: 0,
                        activeInPeriod: 0,
                        inactiveInPeriod: 0,
                        neverLoggedIn: 0,
                        activityRate: 0,
                    };
                }

                const row = data[0];
                return {
                    totalUsers: Number(row.total_users) || 0,
                    activeInPeriod: Number(row.active_in_period) || 0,
                    inactiveInPeriod: Number(row.inactive_in_period) || 0,
                    neverLoggedIn: Number(row.never_logged_in) || 0,
                    activityRate: Number(row.activity_rate) || 0,
                };
            },
            'dataService.getUserActivitySummary',
            { maxAttempts: 2 }
        );
    },

    /**
     * Update user's last login timestamp
     * Uses RPC function: update_user_last_login() (rate-limited to 5-min intervals)
     */
    updateUserLastLogin: async (): Promise<void> => {
        return withRetry(
            async () => {
                const { error } = await rpc('update_user_last_login');
                if (error) throw error;
            },
            'dataService.updateUserLastLogin',
            { maxAttempts: 2 }
        );
    },

    // ============================================
    // ITEM 11: RPC-based complex methods
    // ============================================

    /**
     * Get platform-wide complex statistics (SUPER_ADMIN only)
     * Uses RPC function: get_complex_statistics()
     */
    getPlatformComplexStatistics: async (): Promise<PlatformComplexStatistics> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_complex_statistics');

                if (error) throw error;
                if (!data || data.length === 0) {
                    return {
                        totalComplexes: 0,
                        complexesWithUploads: 0,
                        complexesWithoutUploads: 0,
                        totalUploads: 0,
                        avgUploadsPerComplex: 0,
                        complexesThisMonth: 0,
                    };
                }

                const row = data[0];
                return {
                    totalComplexes: Number(row.total_complexes) || 0,
                    complexesWithUploads: Number(row.complexes_with_uploads) || 0,
                    complexesWithoutUploads: Number(row.complexes_without_uploads) || 0,
                    totalUploads: Number(row.total_uploads) || 0,
                    avgUploadsPerComplex: Number(row.avg_uploads_per_complex) || 0,
                    complexesThisMonth: Number(row.complexes_this_month) || 0,
                };
            },
            'dataService.getPlatformComplexStatistics',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get complex statistics for a specific company
     * Uses RPC function: get_company_complex_statistics(p_company_id)
     */
    getCompanyComplexStatistics: async (companyId: string): Promise<CompanyComplexStatistics> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_company_complex_statistics', {
                    p_company_id: companyId,
                });

                if (error) throw error;
                if (!data || data.length === 0) {
                    return {
                        companyName: '',
                        totalComplexes: 0,
                        totalUploads: 0,
                        pendingUploads: 0,
                        completedUploads: 0,
                        flaggedUploads: 0,
                        avgUploadsPerComplex: 0,
                        totalRevenue: 0,
                    };
                }

                const row = data[0];
                return {
                    companyName: row.company_name || '',
                    totalComplexes: Number(row.total_complexes) || 0,
                    totalUploads: Number(row.total_uploads) || 0,
                    pendingUploads: Number(row.pending_uploads) || 0,
                    completedUploads: Number(row.completed_uploads) || 0,
                    flaggedUploads: Number(row.flagged_uploads) || 0,
                    avgUploadsPerComplex: Number(row.avg_uploads_per_complex) || 0,
                    totalRevenue: Number(row.total_revenue) || 0,
                };
            },
            'dataService.getCompanyComplexStatistics',
            { maxAttempts: 2 }
        );
    },

    /**
     * Search complexes by name or address with fuzzy matching
     * Uses RPC function: search_complexes(p_term, p_company_id, p_limit)
     */
    searchComplexesRPC: async (
        term: string,
        companyId: string | null = null,
        limit: number = 20
    ): Promise<ComplexSearchResult[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('search_complexes', {
                    p_term: term,
                    p_company_id: companyId,
                    p_limit: limit,
                });

                if (error) throw error;
                if (!data) return [];

                return data.map((row: {
                    id: string;
                    name: string;
                    address: string;
                    company_id: string;
                    company_name: string;
                    upload_count: number;
                    similarity_score: number;
                }) => ({
                    id: row.id,
                    name: row.name,
                    address: row.address,
                    companyId: row.company_id,
                    companyName: row.company_name,
                    uploadCount: Number(row.upload_count) || 0,
                    similarityScore: Number(row.similarity_score) || 0,
                }));
            },
            'dataService.searchComplexesRPC',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get paginated complexes with advanced filtering
     * Uses RPC function: get_complexes_paginated(...)
     */
    getComplexesPaginatedRPC: async (
        companyId: string | null = null,
        page: number = 1,
        pageSize: number = 20,
        sortBy: string = 'name',
        sortOrder: 'asc' | 'desc' = 'asc',
        hasUploads: boolean | null = null
    ): Promise<RPCPaginatedComplexesResult> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_complexes_paginated', {
                    p_company_id: companyId,
                    p_page: page,
                    p_page_size: pageSize,
                    p_sort_by: sortBy,
                    p_sort_order: sortOrder,
                    p_has_uploads: hasUploads,
                });

                if (error) throw error;
                if (!data || data.length === 0) {
                    return {
                        complexes: [],
                        totalCount: 0,
                        page: page,
                        pageSize: pageSize,
                        totalPages: 0,
                    };
                }

                const totalCount = Number(data[0]?.total_count) || 0;
                const totalPages = Math.ceil(totalCount / pageSize);

                const complexes = data.map((row: {
                    id: string;
                    name: string;
                    address: string;
                    company_id: string;
                    company_name: string;
                    total_customers: number;
                    upload_count: number;
                    pending_count: number;
                    completed_count: number;
                    created_at: string;
                    updated_at: string;
                }) => ({
                    id: row.id,
                    name: row.name,
                    address: row.address,
                    companyId: row.company_id,
                    companyName: row.company_name,
                    totalCustomers: Number(row.total_customers) || 0,
                    uploadCount: Number(row.upload_count) || 0,
                    pendingCount: Number(row.pending_count) || 0,
                    completedCount: Number(row.completed_count) || 0,
                    createdAt: row.created_at,
                    updatedAt: row.updated_at,
                }));

                return {
                    complexes,
                    totalCount,
                    page,
                    pageSize,
                    totalPages,
                };
            },
            'dataService.getComplexesPaginatedRPC',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get detailed upload summary for a specific complex
     * Uses RPC function: get_complex_upload_summary(p_complex_id)
     */
    getComplexUploadSummary: async (complexId: string): Promise<ComplexUploadSummary> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_complex_upload_summary', {
                    p_complex_id: complexId,
                });

                if (error) throw error;
                if (!data || data.length === 0) {
                    return {
                        complexId: complexId,
                        complexName: '',
                        complexAddress: '',
                        totalUploads: 0,
                        pendingUploads: 0,
                        processingUploads: 0,
                        completedUploads: 0,
                        failedUploads: 0,
                        verifiedCount: 0,
                        flaggedCount: 0,
                        totalRevenue: 0,
                        avgAuthenticityScore: 0,
                        lastUploadAt: null,
                    };
                }

                const row = data[0];
                return {
                    complexId: row.complex_id,
                    complexName: row.complex_name || '',
                    complexAddress: row.complex_address || '',
                    totalUploads: Number(row.total_uploads) || 0,
                    pendingUploads: Number(row.pending_uploads) || 0,
                    processingUploads: Number(row.processing_uploads) || 0,
                    completedUploads: Number(row.completed_uploads) || 0,
                    failedUploads: Number(row.failed_uploads) || 0,
                    verifiedCount: Number(row.verified_count) || 0,
                    flaggedCount: Number(row.flagged_count) || 0,
                    totalRevenue: Number(row.total_revenue) || 0,
                    avgAuthenticityScore: Number(row.avg_authenticity_score) || 0,
                    lastUploadAt: row.last_upload_at || null,
                };
            },
            'dataService.getComplexUploadSummary',
            { maxAttempts: 2 }
        );
    },

    /**
     * Bulk assign uploads to a complex
     * Uses RPC function: assign_uploads_to_complex(p_complex_id, p_upload_ids)
     */
    assignUploadsToComplexRPC: async (
        complexId: string,
        uploadIds: string[]
    ): Promise<{ assignedCount: number; failedCount: number; failedIds: string[] }> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('assign_uploads_to_complex', {
                    p_complex_id: complexId,
                    p_upload_ids: uploadIds,
                });

                if (error) throw error;
                if (!data || data.length === 0) {
                    return {
                        assignedCount: 0,
                        failedCount: uploadIds.length,
                        failedIds: uploadIds,
                    };
                }

                const row = data[0];
                return {
                    assignedCount: Number(row.assigned_count) || 0,
                    failedCount: Number(row.failed_count) || 0,
                    failedIds: row.failed_ids || [],
                };
            },
            'dataService.assignUploadsToComplexRPC',
            { maxAttempts: 2 }
        );
    },

    // ============================================
    // ITEM 12: CUSTOMER TABLE RPC METHODS
    // ============================================

    /**
     * Get customer statistics for a specific complex
     * Uses RPC function: get_complex_customer_statistics(p_complex_id)
     */
    getComplexCustomerStatistics: async (complexId: string): Promise<ComplexCustomerStatistics> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_complex_customer_statistics', {
                    p_complex_id: complexId,
                });

                if (error) throw error;
                if (!data || data.length === 0) {
                    throw new Error(`Complex ${complexId} not found or no access`);
                }

                const row = data[0];
                return {
                    complexName: row.complex_name || '',
                    totalCustomers: Number(row.total_customers) || 0,
                    pendingCustomers: Number(row.pending_customers) || 0,
                    approvedCustomers: Number(row.approved_customers) || 0,
                    rejectedCustomers: Number(row.rejected_customers) || 0,
                    totalRevenue: Number(row.total_revenue) || 0,
                    avgAmountPaid: Number(row.avg_amount_paid) || 0,
                    customersThisMonth: Number(row.customers_this_month) || 0,
                    approvalRate: Number(row.approval_rate) || 0,
                };
            },
            'dataService.getComplexCustomerStatistics',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get aggregated customer statistics for a company
     * Uses RPC function: get_company_customer_statistics(p_company_id)
     */
    getCompanyCustomerStatistics: async (companyId: string): Promise<CompanyCustomerStatistics> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_company_customer_statistics', {
                    p_company_id: companyId,
                });

                if (error) throw error;
                if (!data || data.length === 0) {
                    throw new Error(`Company ${companyId} not found or no access`);
                }

                const row = data[0];
                return {
                    companyName: row.company_name || '',
                    totalCustomers: Number(row.total_customers) || 0,
                    pendingCustomers: Number(row.pending_customers) || 0,
                    approvedCustomers: Number(row.approved_customers) || 0,
                    rejectedCustomers: Number(row.rejected_customers) || 0,
                    totalRevenue: Number(row.total_revenue) || 0,
                    avgAmountPaid: Number(row.avg_amount_paid) || 0,
                    customersThisWeek: Number(row.customers_this_week) || 0,
                    customersThisMonth: Number(row.customers_this_month) || 0,
                    approvalRate: Number(row.approval_rate) || 0,
                    complexCount: Number(row.complex_count) || 0,
                };
            },
            'dataService.getCompanyCustomerStatistics',
            { maxAttempts: 2 }
        );
    },

    /**
     * Search customers by name or email using fuzzy matching
     * Uses RPC function: search_customers(p_search_term, p_complex_id, p_company_id, p_limit)
     */
    searchCustomersRPC: async (
        searchTerm: string,
        options?: { complexId?: string; companyId?: string; limit?: number }
    ): Promise<CustomerSearchResult[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('search_customers', {
                    p_search_term: searchTerm,
                    p_complex_id: options?.complexId || null,
                    p_company_id: options?.companyId || null,
                    p_limit: options?.limit || 20,
                });

                if (error) throw error;
                if (!data) return [];

                return data.map((row: {
                    id: string;
                    name: string;
                    email: string | null;
                    phone: string | null;
                    status: string;
                    complex_id: string;
                    complex_name: string;
                    amount_paid: number;
                    date_submitted: string;
                    similarity_score: number;
                }) => ({
                    id: row.id,
                    name: row.name,
                    email: row.email,
                    phone: row.phone,
                    status: row.status as 'pending' | 'approved' | 'rejected',
                    complexId: row.complex_id,
                    complexName: row.complex_name,
                    amountPaid: Number(row.amount_paid) || 0,
                    dateSubmitted: row.date_submitted,
                    similarityScore: Number(row.similarity_score) || 0,
                }));
            },
            'dataService.searchCustomersRPC',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get paginated customers with optional filters
     * Uses RPC function: get_customers_paginated(...)
     */
    getCustomersPaginatedRPC: async (
        options?: {
            page?: number;
            pageSize?: number;
            complexId?: string;
            companyId?: string;
            status?: 'pending' | 'approved' | 'rejected';
            sortBy?: string;
            sortOrder?: 'asc' | 'desc';
        }
    ): Promise<RPCPaginatedCustomersResult> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_customers_paginated', {
                    p_page: options?.page || 1,
                    p_page_size: options?.pageSize || 20,
                    p_complex_id: options?.complexId || null,
                    p_company_id: options?.companyId || null,
                    p_status: options?.status || null,
                    p_sort_by: options?.sortBy || 'date_submitted',
                    p_sort_order: options?.sortOrder || 'desc',
                });

                if (error) throw error;
                if (!data || data.length === 0) {
                    return {
                        customers: [],
                        totalCount: 0,
                        page: options?.page || 1,
                        pageSize: options?.pageSize || 20,
                        totalPages: 0,
                    };
                }

                const row = data[0];
                const customers = row.customers || [];

                return {
                    customers: customers.map((c: {
                        id: string;
                        name: string;
                        email: string | null;
                        phone: string | null;
                        status: string;
                        date_submitted: string;
                        amount_paid: number;
                        notes: string | null;
                        approved_by: string | null;
                        approved_at: string | null;
                        complex_id: string;
                        complex_name: string;
                        created_at: string;
                        updated_at: string;
                    }) => ({
                        id: c.id,
                        name: c.name,
                        email: c.email,
                        phone: c.phone,
                        status: c.status as 'pending' | 'approved' | 'rejected',
                        dateSubmitted: c.date_submitted,
                        amountPaid: Number(c.amount_paid) || 0,
                        notes: c.notes,
                        approvedBy: c.approved_by,
                        approvedAt: c.approved_at,
                        complexId: c.complex_id,
                        complexName: c.complex_name,
                        createdAt: c.created_at,
                        updatedAt: c.updated_at,
                    })),
                    totalCount: Number(row.total_count) || 0,
                    page: Number(row.page) || 1,
                    pageSize: Number(row.page_size) || 20,
                    totalPages: Number(row.total_pages) || 0,
                };
            },
            'dataService.getCustomersPaginatedRPC',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get pending customers for review queue
     * Uses RPC function: get_pending_customers_for_review(p_complex_id, p_company_id, p_limit)
     */
    getPendingCustomersForReview: async (
        options?: { complexId?: string; companyId?: string; limit?: number }
    ): Promise<PendingCustomerForReview[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_pending_customers_for_review', {
                    p_complex_id: options?.complexId || null,
                    p_company_id: options?.companyId || null,
                    p_limit: options?.limit || 50,
                });

                if (error) throw error;
                if (!data) return [];

                return data.map((row: {
                    id: string;
                    name: string;
                    email: string | null;
                    phone: string | null;
                    complex_id: string;
                    complex_name: string;
                    company_id: string;
                    date_submitted: string;
                    amount_paid: number;
                    notes: string | null;
                    days_pending: number;
                }) => ({
                    id: row.id,
                    name: row.name,
                    email: row.email,
                    phone: row.phone,
                    complexId: row.complex_id,
                    complexName: row.complex_name,
                    companyId: row.company_id,
                    dateSubmitted: row.date_submitted,
                    amountPaid: Number(row.amount_paid) || 0,
                    notes: row.notes,
                    daysPending: Number(row.days_pending) || 0,
                }));
            },
            'dataService.getPendingCustomersForReview',
            { maxAttempts: 2 }
        );
    },

    /**
     * Bulk update customer status (approve/reject)
     * Uses RPC function: update_customer_status(p_customer_ids, p_new_status, p_notes)
     */
    updateCustomerStatusBulk: async (
        customerIds: string[],
        newStatus: 'pending' | 'approved' | 'rejected',
        notes?: string
    ): Promise<{ updatedCount: number; failedIds: string[] }> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('update_customer_status', {
                    p_customer_ids: customerIds,
                    p_new_status: newStatus,
                    p_notes: notes || null,
                });

                if (error) throw error;
                if (!data || data.length === 0) {
                    return {
                        updatedCount: 0,
                        failedIds: customerIds,
                    };
                }

                const row = data[0];
                return {
                    updatedCount: Number(row.updated_count) || 0,
                    failedIds: row.failed_ids || [],
                };
            },
            'dataService.updateCustomerStatusBulk',
            { maxAttempts: 2 }
        );
    },

    // ============================================
    // DATABASE ADMIN METHODS (Item 14)
    // ============================================

    /**
     * Get index usage statistics for all public tables
     * Uses RPC function: get_index_usage_stats()
     * SUPER_ADMIN only
     */
    getIndexUsageStats: async (): Promise<IndexUsageStats[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_index_usage_stats');
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    schemaName: String(row.schema_name || 'public'),
                    tableName: String(row.table_name || ''),
                    indexName: String(row.index_name || ''),
                    indexSize: String(row.index_size || '0 bytes'),
                    idxScan: Number(row.idx_scan) || 0,
                    idxTupRead: Number(row.idx_tup_read) || 0,
                    idxTupFetch: Number(row.idx_tup_fetch) || 0,
                    indexDefinition: String(row.index_definition || ''),
                }));
            },
            'dataService.getIndexUsageStats',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get unused indexes (indexes with low scan count)
     * Uses RPC function: get_unused_indexes(p_min_scans)
     * SUPER_ADMIN only
     */
    getUnusedIndexes: async (minScans: number = 10): Promise<UnusedIndexInfo[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_unused_indexes', {
                    p_min_scans: minScans,
                });
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    tableName: String(row.table_name || ''),
                    indexName: String(row.index_name || ''),
                    indexSize: String(row.index_size || '0 bytes'),
                    scanCount: Number(row.scan_count) || 0,
                    recommendation: String(row.recommendation || ''),
                }));
            },
            'dataService.getUnusedIndexes',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get missing foreign key indexes
     * Uses RPC function: get_missing_fk_indexes()
     * SUPER_ADMIN only
     */
    getMissingFkIndexes: async (): Promise<MissingFkIndex[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_missing_fk_indexes');
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    tableName: String(row.table_name || ''),
                    fkName: String(row.fk_name || ''),
                    fkColumns: String(row.fk_columns || ''),
                    recommendation: String(row.recommendation || ''),
                }));
            },
            'dataService.getMissingFkIndexes',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get table statistics (size, row count, index count)
     * Uses RPC function: get_table_statistics()
     * SUPER_ADMIN only
     */
    getTableStatistics: async (): Promise<TableStatistics[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_table_statistics');
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    tableName: String(row.table_name || ''),
                    rowCount: Number(row.row_count) || 0,
                    tableSize: String(row.table_size || '0 bytes'),
                    indexSize: String(row.index_size || '0 bytes'),
                    totalSize: String(row.total_size || '0 bytes'),
                    indexesCount: Number(row.indexes_count) || 0,
                }));
            },
            'dataService.getTableStatistics',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get index health report (bloat, missing indexes, duplicates)
     * Uses RPC function: get_index_health_report()
     * SUPER_ADMIN only
     */
    getIndexHealthReport: async (): Promise<IndexHealthItem[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_index_health_report');
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    category: String(row.category || ''),
                    tableName: String(row.table_name || ''),
                    itemName: String(row.item_name || ''),
                    currentValue: String(row.current_value || ''),
                    recommendation: String(row.recommendation || ''),
                    priority: String(row.priority || 'low') as 'high' | 'medium' | 'low',
                }));
            },
            'dataService.getIndexHealthReport',
            { maxAttempts: 2 }
        );
    },

    // ============================================
    // STORAGE ADMIN METHODS (Item 15 - esa-uploads bucket optimization)
    // ============================================

    /**
     * Get storage bucket statistics
     * Uses RPC function: get_storage_bucket_stats(p_bucket_id)
     * SUPER_ADMIN only
     */
    getStorageBucketStats: async (bucketId: string = 'esa-uploads'): Promise<StorageBucketStats | null> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_storage_bucket_stats', {
                    p_bucket_id: bucketId,
                });
                if (error) throw error;
                if (!data || data.length === 0) return null;
                const row = data[0];
                return {
                    bucketId: String(row.bucket_id || bucketId),
                    totalFiles: Number(row.total_files) || 0,
                    totalSizeBytes: Number(row.total_size_bytes) || 0,
                    totalSizeMb: Number(row.total_size_mb) || 0,
                    avgFileSizeBytes: Number(row.avg_file_size_bytes) || 0,
                    oldestFile: row.oldest_file ? new Date(String(row.oldest_file)) : null,
                    newestFile: row.newest_file ? new Date(String(row.newest_file)) : null,
                    filesByMonth: row.files_by_month || [],
                };
            },
            'dataService.getStorageBucketStats',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get orphaned storage files (files without database records)
     * Uses RPC function: get_orphaned_storage_files(p_bucket_id, p_limit)
     * SUPER_ADMIN only
     */
    getOrphanedStorageFiles: async (
        bucketId: string = 'esa-uploads',
        limit: number = 100
    ): Promise<OrphanedStorageFile[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_orphaned_storage_files', {
                    p_bucket_id: bucketId,
                    p_limit: limit,
                });
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    filePath: String(row.file_path || ''),
                    fileName: String(row.file_name || ''),
                    fileSize: Number(row.file_size) || 0,
                    mimeType: String(row.mime_type || 'unknown'),
                    createdAt: row.created_at ? new Date(String(row.created_at)) : new Date(),
                    lastAccessedAt: row.last_accessed_at ? new Date(String(row.last_accessed_at)) : null,
                }));
            },
            'dataService.getOrphanedStorageFiles',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get storage access log
     * Uses RPC function: get_storage_access_log(p_bucket_id, p_days, p_limit)
     * SUPER_ADMIN only
     */
    getStorageAccessLog: async (
        bucketId: string = 'esa-uploads',
        days: number = 7,
        limit: number = 100
    ): Promise<StorageAccessLogEntry[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_storage_access_log', {
                    p_bucket_id: bucketId,
                    p_days: days,
                    p_limit: limit,
                });
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    filePath: String(row.file_path || ''),
                    uploadId: row.upload_id ? String(row.upload_id) : null,
                    uploadEmail: row.upload_email ? String(row.upload_email) : null,
                    accessCount: Number(row.access_count) || 0,
                    lastAccessedAt: row.last_accessed_at ? new Date(String(row.last_accessed_at)) : null,
                    createdAt: row.created_at ? new Date(String(row.created_at)) : new Date(),
                    processingStatus: row.processing_status ? String(row.processing_status) : null,
                }));
            },
            'dataService.getStorageAccessLog',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get storage usage by company
     * Uses RPC function: get_storage_usage_by_company()
     * SUPER_ADMIN only
     */
    getStorageUsageByCompany: async (): Promise<CompanyStorageUsage[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_storage_usage_by_company');
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    companyId: String(row.company_id || ''),
                    companyName: String(row.company_name || 'Unknown'),
                    totalFiles: Number(row.total_files) || 0,
                    totalSizeBytes: Number(row.total_size_bytes) || 0,
                    totalSizeMb: Number(row.total_size_mb) || 0,
                    pendingCount: Number(row.pending_count) || 0,
                    completedCount: Number(row.completed_count) || 0,
                }));
            },
            'dataService.getStorageUsageByCompany',
            { maxAttempts: 2 }
        );
    },

    /**
     * Mark orphaned files for cleanup review (does not delete)
     * Uses RPC function: mark_orphaned_files_for_cleanup(p_bucket_id, p_older_than_days)
     * SUPER_ADMIN only
     */
    markOrphanedFilesForCleanup: async (
        bucketId: string = 'esa-uploads',
        olderThanDays: number = 30
    ): Promise<OrphanedFilesCleanupResult> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('mark_orphaned_files_for_cleanup', {
                    p_bucket_id: bucketId,
                    p_older_than_days: olderThanDays,
                });
                if (error) throw error;
                if (!data || data.length === 0) {
                    return { filesMarked: 0, totalSizeBytes: 0, filePaths: [] };
                }
                const row = data[0];
                return {
                    filesMarked: Number(row.files_marked) || 0,
                    totalSizeBytes: Number(row.total_size_bytes) || 0,
                    filePaths: row.file_paths || [],
                };
            },
            'dataService.markOrphanedFilesForCleanup',
            { maxAttempts: 2 }
        );
    },

    /**
     * Validate storage file integrity (check DB records have matching storage files)
     * Uses RPC function: validate_storage_file_integrity(p_bucket_id)
     * SUPER_ADMIN only
     */
    validateStorageFileIntegrity: async (
        bucketId: string = 'esa-uploads'
    ): Promise<StorageIntegrityResult> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('validate_storage_file_integrity', {
                    p_bucket_id: bucketId,
                });
                if (error) throw error;
                if (!data || data.length === 0) {
                    return { totalDbRecords: 0, filesFound: 0, filesMissing: 0, missingFileIds: [] };
                }
                const row = data[0];
                return {
                    totalDbRecords: Number(row.total_db_records) || 0,
                    filesFound: Number(row.files_found) || 0,
                    filesMissing: Number(row.files_missing) || 0,
                    missingFileIds: row.missing_file_ids || [],
                };
            },
            'dataService.validateStorageFileIntegrity',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get comprehensive storage health report
     * Uses RPC function: get_storage_health_report()
     * SUPER_ADMIN only
     */
    getStorageHealthReport: async (): Promise<StorageHealthCheckItem[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_storage_health_report');
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    checkName: String(row.check_name || ''),
                    status: String(row.status || 'UNKNOWN') as 'OK' | 'WARNING' | 'CRITICAL' | 'INFO',
                    details: row.details || {},
                }));
            },
            'dataService.getStorageHealthReport',
            { maxAttempts: 2 }
        );
    },

    // ============================================
    // ANONYMOUS-UPLOADS BUCKET METHODS (Item 16)
    // ============================================

    /**
     * Get anonymous-uploads bucket statistics
     * Uses RPC function: get_anonymous_uploads_bucket_stats()
     * SUPER_ADMIN or COMPANY_OWNER only
     */
    getAnonymousUploadsBucketStats: async (): Promise<AnonymousUploadsBucketStats | null> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_anonymous_uploads_bucket_stats');
                if (error) throw error;
                if (!data || data.length === 0) return null;
                const row = data[0];
                return {
                    bucketId: String(row.bucket_id || 'anonymous-uploads'),
                    totalFiles: Number(row.total_files) || 0,
                    totalSizeBytes: Number(row.total_size_bytes) || 0,
                    totalSizeMb: Number(row.total_size_mb) || 0,
                    avgFileSizeBytes: Number(row.avg_file_size_bytes) || 0,
                    oldestFile: row.oldest_file ? new Date(String(row.oldest_file)) : null,
                    newestFile: row.newest_file ? new Date(String(row.newest_file)) : null,
                    filesByMonth: row.files_by_month || [],
                    filesByCompany: row.files_by_company || [],
                };
            },
            'dataService.getAnonymousUploadsBucketStats',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get orphaned files in anonymous-uploads bucket
     * Uses RPC function: get_orphaned_anonymous_uploads_files(p_limit)
     * SUPER_ADMIN only
     */
    getOrphanedAnonymousUploadsFiles: async (
        limit: number = 100
    ): Promise<OrphanedAnonymousUploadsFile[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_orphaned_anonymous_uploads_files', {
                    p_limit: limit,
                });
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    filePath: String(row.file_path || ''),
                    fileName: String(row.file_name || ''),
                    fileSize: Number(row.file_size) || 0,
                    mimeType: String(row.mime_type || 'unknown'),
                    createdAt: row.created_at ? new Date(String(row.created_at)) : new Date(),
                    lastAccessedAt: row.last_accessed_at ? new Date(String(row.last_accessed_at)) : null,
                    extractedCompanyId: row.extracted_company_id ? String(row.extracted_company_id) : null,
                }));
            },
            'dataService.getOrphanedAnonymousUploadsFiles',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get access log for anonymous-uploads bucket
     * Uses RPC function: get_anonymous_uploads_access_log(p_days, p_limit)
     * SUPER_ADMIN or COMPANY_OWNER only
     */
    getAnonymousUploadsAccessLog: async (
        days: number = 7,
        limit: number = 100
    ): Promise<AnonymousUploadsAccessLogEntry[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_anonymous_uploads_access_log', {
                    p_days: days,
                    p_limit: limit,
                });
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    filePath: String(row.file_path || ''),
                    uploadId: row.upload_id ? String(row.upload_id) : null,
                    uploadEmail: row.upload_email ? String(row.upload_email) : null,
                    companyId: row.company_id ? String(row.company_id) : null,
                    companyName: row.company_name ? String(row.company_name) : null,
                    accessCount: Number(row.access_count) || 0,
                    lastAccessedAt: row.last_accessed_at ? new Date(String(row.last_accessed_at)) : null,
                    createdAt: row.created_at ? new Date(String(row.created_at)) : new Date(),
                    processingStatus: row.processing_status ? String(row.processing_status) : null,
                }));
            },
            'dataService.getAnonymousUploadsAccessLog',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get storage usage by company for anonymous-uploads bucket
     * Uses RPC function: get_anonymous_uploads_usage_by_company()
     * SUPER_ADMIN only
     */
    getAnonymousUploadsUsageByCompany: async (): Promise<AnonymousUploadsCompanyUsage[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_anonymous_uploads_usage_by_company');
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    companyId: String(row.company_id || ''),
                    companyName: String(row.company_name || 'Unknown'),
                    totalFiles: Number(row.total_files) || 0,
                    totalSizeBytes: Number(row.total_size_bytes) || 0,
                    totalSizeMb: Number(row.total_size_mb) || 0,
                    pendingCount: Number(row.pending_count) || 0,
                    completedCount: Number(row.completed_count) || 0,
                    documentsFolderCount: Number(row.documents_folder_count) || 0,
                    uploadsFolderCount: Number(row.uploads_folder_count) || 0,
                }));
            },
            'dataService.getAnonymousUploadsUsageByCompany',
            { maxAttempts: 2 }
        );
    },

    /**
     * Mark orphaned files in anonymous-uploads for cleanup review (does not delete)
     * Uses RPC function: mark_orphaned_anonymous_uploads_for_cleanup(p_older_than_days)
     * SUPER_ADMIN only
     */
    markOrphanedAnonymousUploadsForCleanup: async (
        olderThanDays: number = 30
    ): Promise<OrphanedFilesCleanupResult> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('mark_orphaned_anonymous_uploads_for_cleanup', {
                    p_older_than_days: olderThanDays,
                });
                if (error) throw error;
                if (!data || data.length === 0) {
                    return { filesMarked: 0, totalSizeBytes: 0, filePaths: [] };
                }
                const row = data[0];
                return {
                    filesMarked: Number(row.files_marked) || 0,
                    totalSizeBytes: Number(row.total_size_bytes) || 0,
                    filePaths: row.file_paths || [],
                };
            },
            'dataService.markOrphanedAnonymousUploadsForCleanup',
            { maxAttempts: 2 }
        );
    },

    /**
     * Validate file integrity for anonymous-uploads bucket
     * Uses RPC function: validate_anonymous_uploads_file_integrity()
     * SUPER_ADMIN only
     */
    validateAnonymousUploadsFileIntegrity: async (): Promise<StorageIntegrityResult> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('validate_anonymous_uploads_file_integrity');
                if (error) throw error;
                if (!data || data.length === 0) {
                    return { totalDbRecords: 0, filesFound: 0, filesMissing: 0, missingFileIds: [] };
                }
                const row = data[0];
                return {
                    totalDbRecords: Number(row.total_db_records) || 0,
                    filesFound: Number(row.files_found) || 0,
                    filesMissing: Number(row.files_missing) || 0,
                    missingFileIds: row.missing_file_ids || [],
                };
            },
            'dataService.validateAnonymousUploadsFileIntegrity',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get health report for anonymous-uploads bucket
     * Uses RPC function: get_anonymous_uploads_health_report()
     * SUPER_ADMIN only
     */
    getAnonymousUploadsHealthReport: async (): Promise<StorageHealthCheckItem[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_anonymous_uploads_health_report');
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    checkName: String(row.check_name || ''),
                    status: String(row.status || 'UNKNOWN') as 'OK' | 'WARNING' | 'CRITICAL' | 'INFO',
                    details: row.details || {},
                }));
            },
            'dataService.getAnonymousUploadsHealthReport',
            { maxAttempts: 2 }
        );
    },

    // ============================================
    // ITEM 26: Storage → Database Integration
    // ============================================

    /**
     * Get storage → database sync health metrics
     * Uses RPC function: get_storage_database_sync_health()
     * SUPER_ADMIN only
     */
    getStorageDatabaseSyncHealth: async (): Promise<StorageDatabaseSyncHealth[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_storage_database_sync_health');
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    metricName: String(row.metric_name || ''),
                    metricValue: Number(row.metric_value || 0),
                    description: String(row.description || ''),
                }));
            },
            'dataService.getStorageDatabaseSyncHealth',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get incomplete uploads (stuck in a specific status)
     * Uses RPC function: get_incomplete_uploads(status, older_than_minutes, limit)
     * SUPER_ADMIN only
     */
    getIncompleteUploads: async (
        status: string = 'pending_payment',
        olderThanMinutes: number = 60,
        limit: number = 100
    ): Promise<IncompleteUpload[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_incomplete_uploads', {
                    p_status: status,
                    p_older_than_minutes: olderThanMinutes,
                    p_limit: limit,
                });
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    uploadId: String(row.upload_id || ''),
                    filePath: row.file_path ? String(row.file_path) : null,
                    userEmail: row.user_email ? String(row.user_email) : null,
                    processingStatus: String(row.processing_status || ''),
                    paymentStatus: String(row.payment_status || ''),
                    createdAt: String(row.created_at || ''),
                    ageMinutes: Number(row.age_minutes || 0),
                    correlationId: row.correlation_id ? String(row.correlation_id) : null,
                }));
            },
            'dataService.getIncompleteUploads',
            { maxAttempts: 2 }
        );
    },

    /**
     * Verify if an upload's storage file exists
     * Uses RPC function: verify_upload_storage_exists(upload_id)
     * SUPER_ADMIN only
     */
    verifyUploadStorageExists: async (uploadId: string): Promise<UploadStorageVerification> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('verify_upload_storage_exists', {
                    p_upload_id: uploadId,
                });
                if (error) throw error;
                const row = data?.[0] || {};
                return {
                    uploadId: String(row.upload_id || uploadId),
                    filePath: row.file_path ? String(row.file_path) : null,
                    dbRecordExists: Boolean(row.db_record_exists),
                    expectedBucket: String(row.expected_bucket || 'unknown'),
                    fileSize: row.file_size ? Number(row.file_size) : null,
                    createdAt: row.created_at ? String(row.created_at) : null,
                    processingStatus: row.processing_status ? String(row.processing_status) : null,
                    paymentStatus: row.payment_status ? String(row.payment_status) : null,
                };
            },
            'dataService.verifyUploadStorageExists',
            { maxAttempts: 2 }
        );
    },

    /**
     * Mark an upload for manual review
     * Uses RPC function: mark_upload_for_manual_review(upload_id, reason)
     * SUPER_ADMIN only
     */
    markUploadForManualReview: async (
        uploadId: string,
        reason: string
    ): Promise<ManualReviewResult> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('mark_upload_for_manual_review', {
                    p_upload_id: uploadId,
                    p_reason: reason,
                });
                if (error) throw error;
                const row = data?.[0] || {};
                return {
                    success: Boolean(row.success),
                    message: String(row.message || ''),
                    uploadId: String(row.upload_id || uploadId),
                };
            },
            'dataService.markUploadForManualReview',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get uploads pending manual review
     * Uses RPC function: get_uploads_pending_manual_review(limit)
     * SUPER_ADMIN only
     */
    getUploadsPendingManualReview: async (limit: number = 50): Promise<ManualReviewUpload[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_uploads_pending_manual_review', {
                    p_limit: limit,
                });
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    uploadId: String(row.upload_id || ''),
                    filePath: row.file_path ? String(row.file_path) : null,
                    userEmail: row.user_email ? String(row.user_email) : null,
                    processingStatus: String(row.processing_status || ''),
                    paymentStatus: String(row.payment_status || ''),
                    createdAt: String(row.created_at || ''),
                    reviewReason: row.review_reason ? String(row.review_reason) : null,
                    reviewRequestedAt: row.review_requested_at ? String(row.review_requested_at) : null,
                }));
            },
            'dataService.getUploadsPendingManualReview',
            { maxAttempts: 2 }
        );
    },

    // ============================================
    // ITEM 27: WEBHOOK MONITORING METHODS (Stripe → Webhook integration)
    // ============================================

    /**
     * Get webhook health statistics
     * Uses RPC function: get_webhook_health_stats(p_hours)
     * SUPER_ADMIN only
     */
    getWebhookHealthStats: async (hours: number = 24): Promise<WebhookHealthStats | null> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_webhook_health_stats', {
                    p_hours: hours,
                });
                if (error) throw error;
                if (!data || data.length === 0) return null;

                const row = data[0] as Record<string, unknown>;
                return {
                    totalEvents: Number(row.total_events || 0),
                    successfulEvents: Number(row.successful_events || 0),
                    failedEvents: Number(row.failed_events || 0),
                    signatureFailures: Number(row.signature_failures || 0),
                    skippedEvents: Number(row.skipped_events || 0),
                    successRate: Number(row.success_rate || 100),
                    avgDeliveryLatencyMs: Number(row.avg_delivery_latency_ms || 0),
                    avgProcessingLatencyMs: Number(row.avg_processing_latency_ms || 0),
                    avgTotalLatencyMs: Number(row.avg_total_latency_ms || 0),
                    p50DeliveryLatencyMs: Number(row.p50_delivery_latency_ms || 0),
                    p95DeliveryLatencyMs: Number(row.p95_delivery_latency_ms || 0),
                    p99DeliveryLatencyMs: Number(row.p99_delivery_latency_ms || 0),
                    eventsByType: (row.events_by_type as Record<string, number>) || {},
                    lastEventAt: row.last_event_at ? String(row.last_event_at) : null,
                };
            },
            'dataService.getWebhookHealthStats',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get RLS health status
     * Checks database connection, RLS functions, and context
     * SUPER_ADMIN only
     */
    getRlsHealth: async (): Promise<{
        connectionOk: boolean;
        connectionLatencyMs: number | null;
        rlsFunctionsOk: boolean;
        contextOk: boolean;
        role: string | null;
        checkedAt: string;
        errors?: {
            connection?: string;
            role?: string;
            context?: string;
        };
    }> => {
        const startTime = Date.now();
        const errors: Record<string, string> = {};
        let connectionOk = false;
        let rlsFunctionsOk = false;
        let contextOk = false;
        let role: string | null = null;

        try {
            // Test basic connection
            const { error: connError } = await supabase.from('users').select('id').limit(1);
            connectionOk = !connError;
            if (connError) errors.connection = connError.message;

            // Test RLS helper function
            const { data: roleData, error: roleError } = await rpc('get_auth_user_role');
            rlsFunctionsOk = !roleError;
            role = roleData as string | null;
            if (roleError) errors.role = roleError.message;

            // Test context (company_id lookup)
            const { error: ctxError } = await rpc('get_auth_user_company_id');
            contextOk = !ctxError;
            if (ctxError) errors.context = ctxError.message;
        } catch (error: unknown) {
            errors.connection = error instanceof Error ? error.message : 'Unknown error';
        }

        return {
            connectionOk,
            connectionLatencyMs: Date.now() - startTime,
            rlsFunctionsOk,
            contextOk,
            role,
            checkedAt: new Date().toISOString(),
            errors: Object.keys(errors).length > 0 ? errors : undefined,
        };
    },

    /**
     * Get recent webhook events
     * Uses RPC function: get_recent_webhook_events(p_limit, p_event_type, p_status, p_include_signature_failures)
     * SUPER_ADMIN only
     */
    getRecentWebhookEvents: async (params: {
        limit?: number;
        eventType?: string;
        status?: 'received' | 'processing' | 'completed' | 'failed' | 'skipped';
        includeSignatureFailures?: boolean;
    } = {}): Promise<RecentWebhookEvent[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_recent_webhook_events', {
                    p_limit: params.limit ?? 50,
                    p_event_type: params.eventType ?? null,
                    p_status: params.status ?? null,
                    p_include_signature_failures: params.includeSignatureFailures ?? false,
                });
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    id: String(row.id || ''),
                    stripeEventId: String(row.stripe_event_id || ''),
                    stripeEventType: String(row.stripe_event_type || ''),
                    stripeLivemode: Boolean(row.stripe_livemode),
                    receivedAt: String(row.received_at || ''),
                    processingStatus: String(row.processing_status || 'received') as RecentWebhookEvent['processingStatus'],
                    signatureValid: Boolean(row.signature_valid),
                    deliveryLatencyMs: row.delivery_latency_ms != null ? Number(row.delivery_latency_ms) : null,
                    processingLatencyMs: row.processing_latency_ms != null ? Number(row.processing_latency_ms) : null,
                    uploadId: row.upload_id ? String(row.upload_id) : null,
                    correlationId: String(row.correlation_id || ''),
                    errorMessage: row.error_message ? String(row.error_message) : null,
                }));
            },
            'dataService.getRecentWebhookEvents',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get signature validation failures for debugging
     * Uses RPC function: get_signature_validation_failures(p_hours, p_limit)
     * SUPER_ADMIN only
     */
    getSignatureValidationFailures: async (hours: number = 24, limit: number = 100): Promise<SignatureValidationFailure[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_signature_validation_failures', {
                    p_hours: hours,
                    p_limit: limit,
                });
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    id: String(row.id || ''),
                    stripeEventId: String(row.stripe_event_id || ''),
                    stripeEventType: String(row.stripe_event_type || ''),
                    receivedAt: String(row.received_at || ''),
                    signatureError: row.signature_error ? String(row.signature_error) : null,
                    correlationId: String(row.correlation_id || ''),
                    requestHeaders: (row.request_headers as Record<string, string>) || null,
                }));
            },
            'dataService.getSignatureValidationFailures',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get webhook latency trends (hourly buckets)
     * Uses RPC function: get_webhook_latency_trends(p_hours)
     * SUPER_ADMIN only
     */
    getWebhookLatencyTrends: async (hours: number = 24): Promise<WebhookLatencyTrend[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_webhook_latency_trends', {
                    p_hours: hours,
                });
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    hourBucket: String(row.hour_bucket || ''),
                    eventCount: Number(row.event_count || 0),
                    avgDeliveryLatencyMs: Number(row.avg_delivery_latency_ms || 0),
                    avgProcessingLatencyMs: Number(row.avg_processing_latency_ms || 0),
                    p95DeliveryLatencyMs: Number(row.p95_delivery_latency_ms || 0),
                    successRate: Number(row.success_rate || 100),
                }));
            },
            'dataService.getWebhookLatencyTrends',
            { maxAttempts: 2 }
        );
    },

    /**
     * Get webhook configuration health checks
     * Uses RPC function: get_webhook_configuration_health()
     * SUPER_ADMIN only
     */
    getWebhookConfigurationHealth: async (): Promise<WebhookConfigHealthCheck[]> => {
        return withRetry(
            async () => {
                const { data, error } = await rpc('get_webhook_configuration_health');
                if (error) throw error;
                return (data || []).map((row: Record<string, unknown>) => ({
                    checkName: String(row.check_name || ''),
                    status: (String(row.status || 'healthy')) as WebhookConfigHealthCheck['status'],
                    message: String(row.message || ''),
                    details: (row.details as Record<string, unknown>) || {},
                }));
            },
            'dataService.getWebhookConfigurationHealth',
            { maxAttempts: 2 }
        );
    },
};
